import { memo, Suspense, useMemo, useRef } from 'react'
import { isValidElementType } from 'react-is'
import * as legacyNav from '@panwds/react-nav'
import * as sparkyNav from '@panwds/sparky-react-nav'
import _ from 'lodash'
import { isValidRoute } from './fawkesUtils'
import { renderTitle, getNavBadgeProps, isLazyType } from './navUtils'
import { history } from '../../framework/states'
import { classNames } from '../../framework/utils'
import SubNavItem from './SubNavItem'
import NavIcon from '../common/NavIcon'
import NavErrorBoundary from '../common/NavErrorBoundary'
import './NavActions.scss'
import type { VFC, ElementType, ComponentType } from 'react'
import type { NavItem as NavItemType, NavAction } from '../../framework/microAppTypes'
import type { AnyState, StoreState } from '../../framework/stateTypes'

const isPropsArrayEqual = (previous: AnyState[], next: AnyState[]) => { // only compare two levels deep
  if (previous === next) {
    return true
  }
  if (previous.length !== next.length) {
    return false
  }
  return next.every((each, i) => _.isEqual(each, previous[i]))
}

type IndexedStateArray = AnyState[] & Record<string, AnyState>
type NavActionsProps = {
  isSideNavCollapsed: boolean
  actions?: NavAction[]
  state: StoreState
  activeNavItem?: NavItemType
  activeSubNavItem?: NavItemType,
  useScmNavStyleUpdate?: Boolean
}
const NavActions: VFC<NavActionsProps> = ({ isSideNavCollapsed, actions, state, activeNavItem, activeSubNavItem, useScmNavStyleUpdate }) => {
  const { NavItem } = useScmNavStyleUpdate ? sparkyNav : legacyNav
  const collapsedCls = isSideNavCollapsed ? 'collapsed' : ''
  const prevPropsRef = useRef<IndexedStateArray>()
  const filteredActions = useMemo(() => (
    actions?.filter(act => isValidRoute({ noRBACheck: true, callAvail: true, ...act }))
  ), [actions])

  const stateToProps = useMemo(() => {
    if (!filteredActions?.length) {
      return
    }
    const props = filteredActions.reduce((all, action, i) => {
      const indexed = all as IndexedStateArray
      const props = action.mapStateToProps?.(state)
      if (props) {
        if (action.mspActions) {
          props.mspActions = action.mspActions.map(({ key, navComponent, mapStateToProps }: NavAction) => {
            const NavComponent = navComponent as ComponentType
            const actProps = mapStateToProps?.(state)
            return (
              <NavComponent key={key} {...actProps} />
            )
          })
        }
        indexed.push(props)
        indexed[action.key || `idx${i}`] = props
      }
      return all
    }, [] as AnyState[]) as IndexedStateArray
    if (!props.length) {
      return
    }
    if (prevPropsRef.current && isPropsArrayEqual(prevPropsRef.current, props)) {
      return prevPropsRef.current
    }
    prevPropsRef.current = props
    return props
  }, [filteredActions, state])

  const favorites = state?.preferences?.tsgs?.current?.favorites
  const navItems = useMemo(() => filteredActions?.map((action, i) => {
    let rendered
    const icon = <NavIcon icon={action.icon} className='nav-action-icon' />
    const title = isSideNavCollapsed ? <p className='sparky-left-nav-tooltip'>{renderTitle(action.title)}</p> : renderTitle(action.title)
    const cls = classNames('nav-action-link', collapsedCls, action.className)
    if (action.navComponent && isValidElementType(action.navComponent)) {
      const hasValidPath = Boolean(action.path && !action.path.startsWith('#'))
      const fallback = action.hidden ? null : <NavItem
        title={renderTitle(action.title) as string}
        id={action.key}
        url={action.path || undefined}
        key={action.key}
        icon={<NavIcon icon={action.icon} />}
        noActiveBorder={!hasValidPath && !action.children}
        active={hasValidPath ? undefined : false}
        displayArrow={false}
      />
      const actionCompProps = { isSideNavCollapsed, navAction: action, ...stateToProps?.[action.key || `idx${i}`] }
      if (isLazyType(action.navComponent)) {
        const Component = action.navComponent as ElementType
        rendered = (
          <Suspense fallback={fallback}>
            <Component
              history={history}
              fallback={fallback}
              {...actionCompProps}
              className={cls}
            >
              {icon}
              {title}
            </Component>
          </Suspense>
        )
      }
      else {
        const Component = action.navComponent as ElementType
        rendered = (
          <Component
            history={history}
            fallback={fallback}
            {...actionCompProps}
            className={cls}
          />
        )
      }
      if (action.hidden) {
        rendered = <li className='cm-hidden'>{rendered}</li>
      }
    }
    else if (action.icon && action.title) {
      const title = renderTitle(action.title) as string
      const isActive = !activeNavItem ? undefined : action.path && action.path !== '#' ? activeNavItem.path === action.path : false
      if (action.children) {
        const hasSubNavSibling = action.children.some((child) => child?.children?.length)
        rendered = <NavItem
          title={title}
          id={action.key}
          url={action.path}
          key={action.key}
          active={isActive}
          icon={<NavIcon icon={action.icon} />}
          displayArrow={false}
          {...getNavBadgeProps(action)}
        >
          {action?.children?.filter((child: any) => child?.path || child?.children)?.map((child: any) => {
            return <SubNavItem key={child.key} item={child} activeSubNavItem={activeSubNavItem} hasSubNavSibling={hasSubNavSibling} favorites={favorites}/>
          })}
        </NavItem>
      }
      else {
        rendered = <NavItem
          title={title}
          id={action.key}
          key={action.key}
          url={action.path || '#'}
          icon={<NavIcon icon={action.icon} />}
          active={isActive}
          displayArrow={false}
          {...getNavBadgeProps(action)}
        />
      }
    }
    else {
      console.error('nav action item require key with either navComponent or path+icon+title', action)
      return null
    }
    return (
      <NavErrorBoundary key={`${action.key}-${i}`} context={`action:${action.key} ${action.path || ''}`.trim()}>
        {rendered}
      </NavErrorBoundary>
    )
  }).filter(Boolean) || null, [filteredActions, isSideNavCollapsed, collapsedCls, stateToProps, activeNavItem, activeSubNavItem, favorites, NavItem])
  return <>{navItems}</>
}

export default memo(NavActions)
